import BaseObject from '../../../base/base_object';
import _ from '../../../utils/underscore';
import GameStepInserter from './game_step_inserter';
import GameStepReplacer from './game_step_replacer';
import GameStepBase from '../game_steps/game_step_base';
import GameStepInstanceDescriptor from './GameStepInstanceDescriptor';

export default class GameSequenceBuilder extends BaseObject {
    static CLASS = 'GameSequenceBuilder';

    init(params) {
        super.init(params);
        if(!params.serviceFactory) {
            this.throwArgumentExpected('14', 'init', 'serviceFactory');
        }
        this.serviceFactory = params.serviceFactory;
        this._steps = params.steps || [];
    }

    _steps = [];


    hasStep(gameStepPrototype) {
        if(!this._isGameStep(gameStepPrototype)) {
            this.throwError('25', 'gameStepPrototype argument should be a class that inherits from GameStepBase', {gameStepPrototype});
        }

        let stepIndex = this._steps.findIndex(step => step instanceof gameStepPrototype);

        return stepIndex >= 0;
    }

    getStepInstance(gameStepPrototype) {
        let stepIndex = this._steps.findIndex(step => step instanceof gameStepPrototype);
        if (stepIndex >= 0){
            return this._steps[stepIndex];
        }

        return null;
    }

    insert(gameStepPrototypes, params = null) {
        if(_.isArray(gameStepPrototypes)) {
            if(params) {
                this.throwError('45', 'When `gameStepPrototypes` argument is an array then `params` argument should not be provided! Try provide an array of GameStepInstanceDescriptor');
            }
        }
        return GameStepInserter.create(Object.assign(this._getGameSequenceHelperCommonParameters(), {
            newSteps: this._createMultipleGameStepInstances(gameStepPrototypes, params),
        }));
    }

    replace(gameStepPrototype) {
        if(!this._isGameStep(gameStepPrototype)) {
            this.throwError('55', 'gameStepPrototype argument should be a class that inherits from GameStepBase', {gameStepPrototype});
        }

        return GameStepReplacer.create(Object.assign(this._getGameSequenceHelperCommonParameters(), {
            replacedGameStepPrototype: gameStepPrototype,
            createGameStepsCallback: this._createMultipleGameStepInstances.bind(this)
        }));
    }

    remove(gameStepPrototypes) {

        if(!_.isArray(gameStepPrototypes)) {
            gameStepPrototypes = [gameStepPrototypes];
        }

        gameStepPrototypes.forEach(stepProto => {
            if(!this._isGameStep(stepProto)) {
                this.throwError('72', 'gameStepPrototypes argument should be a class that inherits from GameStepBase', {gameStepPrototype: stepProto});
            }
        });

        let clonedSteps = this._cloneSteps();

        gameStepPrototypes.forEach(stepProto => {
            let stepIndex = clonedSteps.findIndex(step => step instanceof stepProto);
            if(stepIndex >= 0) {
                clonedSteps.splice(stepIndex, 1);
            }
        });

        return this._createGameSequenceBuilder(clonedSteps);

    }

    _getGameSequenceHelperCommonParameters() {
        return {
            currentSteps: this._cloneSteps(),
            createGameSequenceBuilderCallback: this._createGameSequenceBuilder.bind(this),
            isGameStepOrDescriptorCallback: this._isGameStepOrDescriptor.bind(this)
        };
    }

    _createMultipleGameStepInstances(gameStepPrototypes, params) {
        if(!_.isArray(gameStepPrototypes)) { //it means that the caller wants to create just one step
            return [this._createSingleGameStepInstance(gameStepPrototypes, params)];
        }

        // so we create multiple steps
        return gameStepPrototypes.map(stepPrototype => this._createSingleGameStepInstance(stepPrototype));
    }

    _createSingleGameStepInstance(stepPrototypeOrDescriptor, params) {
        if(!this._isGameStepOrDescriptor(stepPrototypeOrDescriptor)) {
            this.throwError('108', `Provided stepPrototypeOrDescriptor argument doesn't inherit from GameStepBase and is not an instance of GameStepInstanceDescriptor either!`, {stepPrototypeOrDescriptor, params});
        }

        // if stepPrototypeOrDescriptor is an instance of GameStepInstanceDescriptor then
        // we create a step according with the information inside the GameStepInstanceDescriptor
        if(this._isStepInstanceDescriptor(stepPrototypeOrDescriptor)) {
            return stepPrototypeOrDescriptor.stepPrototype.create(Object.assign({
                serviceFactory: this.serviceFactory
            }, stepPrototypeOrDescriptor.stepParams))
        }

        // here we have a game step class so we just call the create method of that game step.
        return stepPrototypeOrDescriptor.create(Object.assign({
            serviceFactory: this.serviceFactory
        }, params));
    }


    _isGameStep(stepPrototype) {
        if(_.isNullOrUndefined(stepPrototype)) {
            return false;
        }

        if(stepPrototype instanceof GameStepBase) {
            return true;
        }

        if(stepPrototype.prototype instanceof GameStepBase) {
            return true;
        }


        return false;
    }

    _isStepInstanceDescriptor(descriptor) {
        if(_.isNullOrUndefined(descriptor)) {
            return false;
        }

        if(descriptor.prototype instanceof GameStepInstanceDescriptor) {
            return true;
        }

        if(descriptor instanceof GameStepInstanceDescriptor) {
            return true;
        }

        return false;
    }

    _isGameStepOrDescriptor(stepPrototypeOrDescriptor) {

        if(this._isGameStep(stepPrototypeOrDescriptor)) {
            return true;
        }

        if(this._isStepInstanceDescriptor(stepPrototypeOrDescriptor)) {
            return true;
        }

        return false;
    }

    _cloneSteps() {
        return [].concat(this._steps);
    }

    build() {
        let duplicatedSteps = [];
        for(let i = 0; i < this._steps.length - 1; i++) {
            for(let k = i + 1; k < this._steps.length; k++) {
                if(this._steps[i].constructor === this._steps[k].constructor) {
                    duplicatedSteps.push(this._steps[i]);
                }
            }
        }

        if(duplicatedSteps.length > 0) {
            const msg = 'Duplicated game steps detected';
            this.logger.error(msg, ...duplicatedSteps);
            throw new Error(msg);
        }
        return this._cloneSteps();
    }

    _createGameSequenceBuilder = (steps) => {
        return GameSequenceBuilder.create({
            serviceFactory: this.serviceFactory,
            steps: steps
        })
    }
}